import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "./api";

const KEY = "bp_theme";
const ThemeContext = createContext({ pref: "default", theme: "light", setPref: () => {} });

function routeDefault(path) {
  return path.startsWith("/t/") ? "dark" : "light";
}

function systemTheme() {
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

export function resolveTheme(pref, path) {
  if (pref === "light" || pref === "dark") return pref;
  if (pref === "system") return systemTheme();
  return routeDefault(path);
}

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function ThemeProvider({ children }) {
  const location = useLocation();
  const [pref, setPrefState] = useState(() => localStorage.getItem(KEY) || "default");
  const theme = resolveTheme(pref, location.pathname);

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const h = () => applyTheme(systemTheme());
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [pref]);

  useEffect(() => {
    const h = (e) => {
      if (e.detail && e.detail !== localStorage.getItem(KEY)) {
        localStorage.setItem(KEY, e.detail);
        setPrefState(e.detail);
      }
    };
    window.addEventListener("bp:theme", h);
    return () => window.removeEventListener("bp:theme", h);
  }, []);

  const setPref = useCallback((p) => {
    localStorage.setItem(KEY, p);
    setPrefState(p);
    if (localStorage.getItem("bp_auth_cache")) {
      api.post("/identity/me/theme", { theme: p }).catch(() => {});
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ pref, theme, setPref }}>
      {children}
      <Toaster position="top-center" richColors theme={theme} />
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/* Chart palette — recharts needs concrete color strings. hex-ok */
const CHART = {
  light: { tick: "#6B6280", grid: "#EDE6DA", cursor: "rgba(26,16,48,0.06)", tooltipBg: "#FFFFFF", tooltipBorder: "#EDE6DA", tooltipText: "#1A1030", bar1: "#1A1030", bar2: "#FF8C3B", teal: "#3FA9B8", line: "#B45309" }, // hex-ok
  dark: { tick: "#F6EFE280", grid: "#3B2B60", cursor: "rgba(246,239,226,0.06)", tooltipBg: "#2A1B4A", tooltipBorder: "#3B2B60", tooltipText: "#F6EFE2", bar1: "#FF8C3B", bar2: "#FFD166", teal: "#8BD3DD", line: "#FF8C3B" }, // hex-ok
};

export function useChartColors() {
  const { theme } = useTheme();
  return CHART[theme] || CHART.light;
}
